import { useState, useRef, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  Box,
  Flex,
  Input,
  Select,
  Text,
  Heading,
  useOutsideClick,
  Collapse,
  Divider,
} from "@chakra-ui/react"
import { FaFilter } from "react-icons/fa"
import { ArrowDownIcon2 } from "@/styles/icons/ArrowDownIcon2"
import { Badge, Button } from "barte-design-system"

export interface SubscriptionFilterState {
  status: string
  startDate: string | null
  endDate: string | null
}

interface SubscriptionTableFilterProps {
  onFilterChange: (filterState: SubscriptionFilterState) => void
  defaultValues?: SubscriptionFilterState
  appliedCount?: number
}

const statusOptions = [
  { label: "Todas", value: "ALL" },
  { label: "Ativas", value: "ACTIVE" },
  { label: "Inativas", value: "INACTIVE" },
  { label: "Pendentes", value: "PENDING" },
  { label: "Inadimplentes", value: "DEFAULTER" },
]

export const initialSubscriptionFilterState: SubscriptionFilterState = {
  status: "ALL",
  startDate: null,
  endDate: null,
}

const filterSections = [
  { id: "status", label: "Status" },
  { id: "data", label: "Data de contratação" },
]

function getSectionCurrentValue(id: string, filterState: SubscriptionFilterState): string {
  if (id === "status") {
    return statusOptions.find((opt) => opt.value === filterState.status)?.label ?? ""
  }
  if (id === "data") {
    if (filterState.startDate && filterState.endDate) return `${filterState.startDate} – ${filterState.endDate}`
    if (filterState.startDate) return `De ${filterState.startDate}`
    if (filterState.endDate) return `Até ${filterState.endDate}`
    return ""
  }
  return ""
}

export function SubscriptionTableFilter({
  onFilterChange,
  defaultValues,
  appliedCount = 0,
}: SubscriptionTableFilterProps) {
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [activeFilters, setActiveFilters] = useState<string[]>([])
  const [filterState, setFilterState] = useState<SubscriptionFilterState>({
    ...initialSubscriptionFilterState,
    ...defaultValues,
  })
  const filterRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setFilterState({
      status: defaultValues?.status ?? "ALL",
      startDate: defaultValues?.startDate ?? null,
      endDate: defaultValues?.endDate ?? null,
    })
  }, [defaultValues?.status, defaultValues?.startDate, defaultValues?.endDate])

  useOutsideClick({
    ref: filterRef,
    handler: () => setIsFilterOpen(false),
  })

  const toggleFilter = () => setIsFilterOpen(!isFilterOpen)

  const toggleFilterOption = (id: string) => {
    if (activeFilters.includes(id)) {
      setActiveFilters(activeFilters.filter((filterId) => filterId !== id))
    } else {
      setActiveFilters([...activeFilters, id])
    }
  }

  const handleClearFilters = () => {
    setFilterState(initialSubscriptionFilterState)
    setIsFilterOpen(false)
    onFilterChange(initialSubscriptionFilterState)
  }

  const applyFilters = () => {
    setIsFilterOpen(false)
    onFilterChange(filterState)
  }

  return (
    <Box ref={filterRef}>
      <Box position="relative" display="inline-flex">
        <Button
          onClick={toggleFilter}
          variant="outline"
          iconLeft={<FaFilter size="14px" color="currentColor" />}
          size="sm"
          iconRight={appliedCount > 0 ? <Badge tone="brand" size="base">{appliedCount}</Badge> : null}
        >
          <Text fontSize="16px" fontWeight="400">
            Filtro
          </Text>
        </Button>
      </Box>

      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 0 }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            transition={{ duration: 0.2 }}
            style={{ position: "relative" }}
          >
            <Box
              position="absolute"
              right="0"
              mt="2"
              w="72"
              bg="white"
              borderWidth="1px"
              borderRadius="md"
              boxShadow="lg"
              zIndex="20"
              overflow="hidden"
            >
              <Flex justify="space-between" align="center" p="4" borderBottomWidth="1px">
                <Heading as="h3" size="sm" fontWeight="medium">
                  Filtros
                </Heading>
                <Button variant="link" size="sm" onClick={handleClearFilters}>
                  Limpar Todos
                </Button>
              </Flex>

              <Box p="4" maxH="96" overflowY="auto">
                {filterSections.map((section) => (
                  <Box key={section.id} mb="4">
                    <Flex
                      align="center"
                      justify="space-between"
                      cursor="pointer"
                      mb="2"
                      onClick={() => toggleFilterOption(section.id)}
                    >
                      <Text>{section.label}</Text>
                      <Flex align="center" gap="2">
                        <Text fontSize="12px" color="gray.400">
                          {getSectionCurrentValue(section.id, filterState)}
                        </Text>
                        <Box
                          transition="transform 0.2s"
                          transform={activeFilters.includes(section.id) ? "rotate(180deg)" : "rotate(0deg)"}
                        >
                          <ArrowDownIcon2 />
                        </Box>
                      </Flex>
                    </Flex>

                    <Collapse in={activeFilters.includes(section.id)}>
                      {section.id === "status" && (
                        <Box pl="6" mt="2">
                          <Select
                            size="sm"
                            value={filterState.status}
                            onChange={(e) =>
                              setFilterState((prev) => ({ ...prev, status: e.target.value }))
                            }
                            _focus={{ border: "1px solid", borderColor: "pink.500" }}
                          >
                            {statusOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </Select>
                        </Box>
                      )}

                      {section.id === "data" && (
                        <Flex pl="6" mt="2" gap="2">
                          <Input
                            type="date"
                            size="sm"
                            value={filterState.startDate || ""}
                            onChange={(e) =>
                              setFilterState((prev) => ({
                                ...prev,
                                startDate: e.target.value || null,
                              }))
                            }
                            _focus={{ border: "1px solid", borderColor: "pink.500" }}
                          />
                          <Input
                            type="date"
                            size="sm"
                            value={filterState.endDate || ""}
                            onChange={(e) =>
                              setFilterState((prev) => ({
                                ...prev,
                                endDate: e.target.value || null,
                              }))
                            }
                            _focus={{ border: "1px solid", borderColor: "pink.500" }}
                          />
                        </Flex>
                      )}
                    </Collapse>
                  </Box>
                ))}
              </Box>

              <Divider />
              <Flex p="4" justify="flex-end">
                <Button variant="fill" size="sm" onClick={applyFilters}>
                  Aplicar Filtros
                </Button>
              </Flex>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  )
}
